import { createApi } from '@reduxjs/toolkit/query/react';

import { readAssistantSession } from './session';

import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';

import { resolveMomentsBaseUrl } from '@/store/api/momentsApi';

// MEN-5598: the scheduling assistant's own RTK Query slice. It talks to the
// Moments `/api/v1/scheduling-assistant/*` routes with the assistant session
// token only: no cookies (`credentials: 'omit'`), no member token, and no
// fallback to the signed-in member's Moments base URL.

export interface AssistantQueryError {
  data?: unknown;
  status: number | 'FETCH_ERROR' | 'MOMENTS_BASE_URL_MISSING';
}

export interface AssistantQueryArgs {
  /** Sends the assistant session token. Public routes leave it off. */
  authenticated?: boolean;
  body?: unknown;
  method?: 'DELETE' | 'GET' | 'POST';
  /**
   * A body sent exactly as given. Commands use it so a replay
   * after a lost response carries the same bytes as the first send.
   */
  rawBody?: string;
  url: string;
}

const parseJsonSafely = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const assistantBaseQuery: BaseQueryFn<
  AssistantQueryArgs,
  unknown,
  AssistantQueryError
> = async (
  { authenticated = false, body, method = 'GET', rawBody, url },
  { signal },
) => {
  const baseUrl = resolveMomentsBaseUrl({
    envUrl: import.meta.env.VITE_MOMENTS_BASE_URL as string | undefined,
  });
  if (!baseUrl) {
    return { error: { status: 'MOMENTS_BASE_URL_MISSING' } };
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (authenticated) {
    const token = readAssistantSession()?.token;
    if (!token) {
      return { error: { data: { error: 'unauthorized' }, status: 401 } };
    }
    headers.Authorization = `Bearer ${token}`;
  }
  const payload =
    rawBody ?? (body === undefined ? undefined : JSON.stringify(body));
  if (payload !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  try {
    const response = await fetch(`${baseUrl}${url}`, {
      body: payload,
      cache: 'no-store',
      credentials: 'omit',
      headers,
      method,
      signal,
    });
    const data = await parseJsonSafely(response);
    if (!response.ok) {
      // A 401 is handled by the page (useEndAssistantSessionOnUnauthorized),
      // which forgets the token and says the link expired.
      return { error: { data, status: response.status } };
    }
    return { data };
  } catch (error) {
    return {
      error: {
        data: error instanceof Error ? error.message : error,
        status: 'FETCH_ERROR',
      },
    };
  }
};

/** The error code Moments puts in `{"error": "..."}`, when there is one. */
export const assistantErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return undefined;
  }
  const { data } = error as { data?: unknown };
  if (!data || typeof data !== 'object' || !('error' in data)) {
    return undefined;
  }
  const { error: code } = data as { error?: unknown };
  return typeof code === 'string' ? code : undefined;
};

export const assistantErrorStatus = (
  error: unknown,
): AssistantQueryError['status'] | undefined => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return undefined;
  }
  return (error as AssistantQueryError).status;
};

export type AssistantExternalRefKind = 'appointment' | 'series';

export interface AssistantExternalRef {
  id: string;
  kind: AssistantExternalRefKind;
}

export interface AssistantAccessRequest {
  email: string;
  externalRef?: AssistantExternalRef;
}

export interface AssistedMember {
  displayName: string;
  memberId: string;
}

export type AssistantAppointmentKind = 'chemistry' | 'coaching';

export interface AssistantAppointmentCapabilities {
  /** One occurrence only; an assistant never changes a whole series. */
  canCancelOccurrence: boolean;
  /** One occurrence only; an assistant never changes a whole series. */
  canRescheduleOccurrence: boolean;
}

export interface AssistantAppointment {
  capabilities: AssistantAppointmentCapabilities;
  coachDisplayName: string;
  endAt: string;
  id: string;
  kind: AssistantAppointmentKind;
  /** `scheduled`, `reschedule_pending`, `cancellation_pending`, `cancelled`. */
  lifecycleStatus: string;
  seriesId: string | null;
  /** Preimage for a reschedule or cancel command. */
  seriesMutationVersion: number | null;
  startAt: string;
  timezone: string;
  /** Preimage for a reschedule or cancel command. */
  version: number;
}

export interface AssistantAvailabilityDay {
  date: string;
  enabled: boolean;
  slots: { endAt: string; label: string; startAt: string }[];
}

export interface AssistantSchedulingContext {
  appointmentId: string;
  days: AssistantAvailabilityDay[];
  durationMinutes: number;
  eligible: boolean;
  timezone: string;
}

export type AssistantAction = 'cancel' | 'reschedule';

/** What an assistant can book with a coach: the member's own booking kinds. */
export type AssistantBooking = 'chemistry' | 'series' | 'single';

/** The next thing to book with a coach, by the member's coach-card rule. */
export type AssistantCoachNextStep =
  | 'book_chemistry'
  | 'book_series'
  | 'chemistry_booked'
  | 'not_bookable'
  | 'series_booked';

/** A coach the member is matched to: first name and picture only. */
export interface AssistantCoach {
  /** True once the member has met them: a one-off session can be booked. */
  canBookSingle: boolean;
  firstName: string;
  nextStep: AssistantCoachNextStep;
  personId: string;
  pictureUrl: string | null;
}

export interface AssistantBookSlot {
  /** Series only: the starts the coach is busy for, when there are any. */
  coachConflictStarts: string[];
  endAt: string;
  label: string;
  startAt: string;
}

export interface AssistantBookContext {
  /** Series only; the server decides it, never the client. */
  appointmentCount: number | null;
  booking: AssistantBooking;
  /** Series only. */
  cadenceWeeks: number | null;
  coachPersonId: string;
  days: (Omit<AssistantAvailabilityDay, 'slots'> & {
    slots: AssistantBookSlot[];
  })[];
  durationMinutes: number;
  eligible: boolean;
  /** Why nothing is offered, e.g. `active_series_exists`. */
  ineligibleReasons: string[];
  timezone: string;
}

export type AssistantOperationStatus =
  'confirmed' | 'needs_reconciliation' | 'pending' | 'rejected';

/** A mutation result or a poll of its operation: the same fields matter. */
export interface AssistantOperationResult {
  appointment: AssistantAppointment | null;
  declineCode: string | null;
  operationId: string;
  retryAfterSeconds: number | null;
  status: AssistantOperationStatus;
}

interface AssistedMemberWire {
  display_name: string;
  member_id: string;
}

interface AssistantAppointmentWire {
  capabilities: {
    can_cancel_occurrence: boolean;
    can_reschedule_occurrence: boolean;
  };
  coach_display_name: string;
  end_at: string;
  id: string;
  kind: AssistantAppointmentKind;
  lifecycle_status: string;
  series_id: string | null;
  series_mutation_version: number | null;
  start_at: string;
  timezone: string;
  version: number;
}

interface AssistantSchedulingContextWire {
  appointment_id: string;
  days: {
    date: string;
    enabled: boolean;
    slots: { end_at: string; label: string; start_at: string }[];
  }[];
  duration_minutes: number;
  eligibility: { eligible: boolean };
  timezone: string;
}

interface AssistantCoachWire {
  can_book_single: boolean;
  coach_first_name: string;
  coach_person_id: string;
  coach_picture_url: string | null;
  next_step: AssistantCoachNextStep;
}

interface AssistantBookContextWire {
  appointment_count: number | null;
  booking: AssistantBooking;
  cadence_weeks: number | null;
  coach_person_id: string;
  days: {
    date: string;
    enabled: boolean;
    slots: {
      coach_conflict?: { conflicting_occurrence_starts: string[] };
      end_at: string;
      label: string;
      start_at: string;
    }[];
  }[];
  duration_minutes: number;
  eligibility: { eligible: boolean; reasons?: string[] };
  timezone: string;
}

interface AssistantOperationWire {
  appointment: AssistantAppointmentWire | null;
  decline_code: string | null;
  retry_after_seconds: number | null;
  status: AssistantOperationStatus;
}

const toAppointment = (
  appointment: AssistantAppointmentWire,
): AssistantAppointment => ({
  capabilities: {
    canCancelOccurrence: appointment.capabilities.can_cancel_occurrence,
    canRescheduleOccurrence: appointment.capabilities.can_reschedule_occurrence,
  },
  coachDisplayName: appointment.coach_display_name,
  endAt: appointment.end_at,
  id: appointment.id,
  kind: appointment.kind,
  lifecycleStatus: appointment.lifecycle_status,
  seriesId: appointment.series_id,
  seriesMutationVersion: appointment.series_mutation_version,
  startAt: appointment.start_at,
  timezone: appointment.timezone,
  version: appointment.version,
});

const toOperationResult = (
  operationId: string,
  wire: AssistantOperationWire,
): AssistantOperationResult => ({
  appointment: wire.appointment ? toAppointment(wire.appointment) : null,
  declineCode: wire.decline_code,
  operationId,
  retryAfterSeconds: wire.retry_after_seconds,
  status: wire.status,
});

export interface AssistantSessionGrant {
  expiresAt: string;
  token: string;
}

const API_PREFIX = '/api/v1/scheduling-assistant';

const memberPath = (memberId: string) =>
  `${API_PREFIX}/members/${encodeURIComponent(memberId)}`;

const appointmentPath = (memberId: string, appointmentId: string) =>
  `${memberPath(memberId)}/appointments/${encodeURIComponent(appointmentId)}`;

export interface AssistantAppointmentArgs {
  appointmentId: string;
  memberId: string;
}

export interface AssistantCommandArgs extends AssistantAppointmentArgs {
  action: AssistantAction;
  /** The exact JSON body; see AssistantQueryArgs.rawBody. */
  requestBytes: string;
}

export interface AssistantBookContextArgs {
  booking: AssistantBooking;
  coachPersonId: string;
  memberId: string;
  viewerTimezone: string;
}

export interface AssistantBookArgs {
  memberId: string;
  /** The exact JSON body; see AssistantQueryArgs.rawBody. */
  requestBytes: string;
}

export const schedulingAssistantApi = createApi({
  baseQuery: assistantBaseQuery,
  endpoints: (builder) => ({
    confirmAssistant: builder.mutation<{ status: string }, string>({
      query: (token) => ({
        body: { token },
        method: 'POST',
        url: `${API_PREFIX}/confirmations`,
      }),
    }),
    exchangeAssistantLink: builder.mutation<AssistantSessionGrant, string>({
      query: (token) => ({
        body: { token },
        method: 'POST',
        url: `${API_PREFIX}/sessions`,
      }),
      transformResponse: (response: { expires_at: string; token: string }) => ({
        expiresAt: response.expires_at,
        token: response.token,
      }),
    }),
    getAssistantAppointment: builder.query<
      AssistantAppointment,
      AssistantAppointmentArgs
    >({
      providesTags: (_result, _error, { appointmentId }) => [
        { id: appointmentId, type: 'AssistantAppointment' },
      ],
      query: ({ appointmentId, memberId }) => ({
        authenticated: true,
        url: appointmentPath(memberId, appointmentId),
      }),
      transformResponse: (response: {
        appointment: AssistantAppointmentWire;
      }) => toAppointment(response.appointment),
    }),
    getAssistantAppointments: builder.query<AssistantAppointment[], string>({
      providesTags: [{ id: 'LIST', type: 'AssistantAppointment' }],
      query: (memberId) => ({
        authenticated: true,
        url: `${memberPath(memberId)}/appointments`,
      }),
      transformResponse: (response: {
        appointments: AssistantAppointmentWire[];
      }) => response.appointments.map(toAppointment),
    }),
    getAssistantBookContext: builder.query<
      AssistantBookContext,
      AssistantBookContextArgs
    >({
      query: ({ booking, coachPersonId, memberId, viewerTimezone }) => ({
        authenticated: true,
        url: `${memberPath(memberId)}/coaches/${encodeURIComponent(coachPersonId)}/scheduling-context?booking=${encodeURIComponent(booking)}&viewer_timezone=${encodeURIComponent(viewerTimezone)}`,
      }),
      transformResponse: (response: AssistantBookContextWire) => ({
        appointmentCount: response.appointment_count,
        booking: response.booking,
        cadenceWeeks: response.cadence_weeks,
        coachPersonId: response.coach_person_id,
        days: response.days.map((day) => ({
          date: day.date,
          enabled: day.enabled,
          slots: day.slots.map((slot) => ({
            coachConflictStarts:
              slot.coach_conflict?.conflicting_occurrence_starts ?? [],
            endAt: slot.end_at,
            label: slot.label,
            startAt: slot.start_at,
          })),
        })),
        durationMinutes: response.duration_minutes,
        eligible: response.eligibility.eligible,
        ineligibleReasons: response.eligibility.reasons ?? [],
        timezone: response.timezone,
      }),
    }),
    getAssistantCoaches: builder.query<AssistantCoach[], string>({
      providesTags: ['AssistantCoaches'],
      query: (memberId) => ({
        authenticated: true,
        url: `${memberPath(memberId)}/coaches`,
      }),
      transformResponse: (response: { coaches: AssistantCoachWire[] }) =>
        response.coaches.map((coach) => ({
          canBookSingle: coach.can_book_single,
          firstName: coach.coach_first_name,
          nextStep: coach.next_step,
          personId: coach.coach_person_id,
          pictureUrl: coach.coach_picture_url,
        })),
    }),
    getAssistantOperation: builder.query<
      AssistantOperationResult,
      { memberId: string; operationId: string }
    >({
      query: ({ memberId, operationId }) => ({
        authenticated: true,
        url: `${memberPath(memberId)}/appointment-operations/${encodeURIComponent(operationId)}`,
      }),
      transformResponse: (response: AssistantOperationWire & { id: string }) =>
        toOperationResult(response.id, response),
    }),
    getAssistantSchedulingContext: builder.query<
      AssistantSchedulingContext,
      AssistantAppointmentArgs & { viewerTimezone: string }
    >({
      query: ({ appointmentId, memberId, viewerTimezone }) => ({
        authenticated: true,
        url: `${appointmentPath(memberId, appointmentId)}/scheduling-context?viewer_timezone=${encodeURIComponent(viewerTimezone)}`,
      }),
      transformResponse: (response: AssistantSchedulingContextWire) => ({
        appointmentId: response.appointment_id,
        days: response.days.map((day) => ({
          date: day.date,
          enabled: day.enabled,
          slots: day.slots.map((slot) => ({
            endAt: slot.end_at,
            label: slot.label,
            startAt: slot.start_at,
          })),
        })),
        durationMinutes: response.duration_minutes,
        eligible: response.eligibility.eligible,
        timezone: response.timezone,
      }),
    }),
    getAssistedMembers: builder.query<AssistedMember[], void>({
      providesTags: ['AssistedMembers'],
      query: () => ({ authenticated: true, url: `${API_PREFIX}/members` }),
      transformResponse: (response: { members: AssistedMemberWire[] }) =>
        response.members.map((member) => ({
          displayName: member.display_name,
          memberId: member.member_id,
        })),
    }),
    // Always 202 with no body, whether or not the address is an assistant.
    requestAssistantAccess: builder.mutation<void, AssistantAccessRequest>({
      query: ({ email, externalRef }) => ({
        body: {
          email,
          ...(externalRef ? { external_ref: externalRef } : {}),
        },
        method: 'POST',
        url: `${API_PREFIX}/access-requests`,
      }),
    }),
    signOutAssistant: builder.mutation<void, void>({
      query: () => ({
        authenticated: true,
        method: 'DELETE',
        url: `${API_PREFIX}/sessions/current`,
      }),
    }),
    // Count, cadence and seat type come from the member's context on the
    // server; the body names only the coach, the booking and the start.
    submitAssistantBook: builder.mutation<
      AssistantOperationResult,
      AssistantBookArgs
    >({
      query: ({ memberId, requestBytes }) => ({
        authenticated: true,
        method: 'POST',
        rawBody: requestBytes,
        url: `${memberPath(memberId)}/appointments`,
      }),
      transformResponse: (
        response: AssistantOperationWire & { operation_id: string },
      ) => toOperationResult(response.operation_id, response),
    }),
    // One occurrence only: the body has no scope, and Moments sets it.
    submitAssistantCommand: builder.mutation<
      AssistantOperationResult,
      AssistantCommandArgs
    >({
      query: ({ action, appointmentId, memberId, requestBytes }) => ({
        authenticated: true,
        method: 'POST',
        rawBody: requestBytes,
        url: `${appointmentPath(memberId, appointmentId)}/${action}`,
      }),
      transformResponse: (
        response: AssistantOperationWire & { operation_id: string },
      ) => toOperationResult(response.operation_id, response),
    }),
  }),
  reducerPath: 'schedulingAssistantApi',
  tagTypes: ['AssistantAppointment', 'AssistantCoaches', 'AssistedMembers'],
});

export const {
  useConfirmAssistantMutation,
  useExchangeAssistantLinkMutation,
  useGetAssistantAppointmentQuery,
  useGetAssistantAppointmentsQuery,
  useGetAssistantBookContextQuery,
  useGetAssistantCoachesQuery,
  useGetAssistantSchedulingContextQuery,
  useGetAssistedMembersQuery,
  useRequestAssistantAccessMutation,
  useSignOutAssistantMutation,
} = schedulingAssistantApi;
